import { z } from "zod";
import type { FieldErrors, Resolver } from "react-hook-form";
import type { ProviderValues } from "@/pages/Providers/types";

const optionalText = z.string().trim().max(255, "providers.errors.max");
const requiredText = optionalText.min(1, "providers.errors.required");
const catalog = z.number().int().positive().nullable();
const requiredCatalog = catalog.refine(
    (value) => value !== null,
    "providers.errors.required"
);
const phone = optionalText.refine(
    (value) =>
        value === "" ||
        (/^(?:\+|00)?[0-9][0-9\s().-]*$/.test(value) &&
            value.replace(/\D/g, "").replace(/^00/, "").length >= 7 &&
            value.replace(/\D/g, "").replace(/^00/, "").length <= 15),
    "providers.errors.phone"
);

export const providerSchema: z.ZodType<ProviderValues> = z.object({
    name: requiredText,
    trading_name: optionalText,
    tax_id: z
        .string()
        .trim()
        .min(1, "providers.errors.required")
        .max(32, "providers.errors.max"),
    email: z.email("providers.errors.email").max(255, "providers.errors.max"),
    phone: phone,
    fax: phone,
    website: optionalText.refine((value) => {
        if (!value) {
            return true;
        }
        try {
            const url = new URL(
                value.includes("://") ? value : `https://${value}`
            );
            return (
                ["http:", "https:"].includes(url.protocol) &&
                url.hostname.includes(".")
            );
        } catch {
            return false;
        }
    }, "providers.errors.url"),
    provider_type_id: requiredCatalog,
    speciality_ids: z.array(z.number().int().positive()).max(100),
    notes: z.string().max(20000, "providers.errors.max"),
    address: optionalText,
    address_extra: optionalText,
    postal_code: z.string().trim().max(20, "providers.errors.max"),
    city: optionalText,
    province: optionalText,
    country_id: requiredCatalog,
    legal_name: optionalText,
    legal_tax_id: z.string().trim().max(32, "providers.errors.max"),
    legal_notary: optionalText,
    legal_protocol: optionalText,
    legal_power_date: z
        .string()
        .refine(
            (value) => value === "" || /^\d{4}-\d{2}-\d{2}$/.test(value),
            "providers.errors.date_format"
        ),
    payment_method_id: catalog,
    validation_state_id: catalog,
    retention: z
        .number()
        .int("providers.errors.integer")
        .min(0, "providers.errors.min")
        .max(100, "providers.errors.max")
        .nullable(),
    guarantee: z
        .number()
        .min(0, "providers.errors.min")
        .max(999999999.99, "providers.errors.max")
        .nullable(),
    payment_blocked: z.boolean(),
});

export const providerResolver: Resolver<ProviderValues> = async (values) => {
    const result = providerSchema.safeParse(values);
    if (result.success) {
        return { values: result.data, errors: {} };
    }
    const errors = Object.fromEntries(
        result.error.issues.map((issue) => [
            issue.path[0],
            { type: issue.code, message: issue.message },
        ])
    );
    return { values: {}, errors: errors as FieldErrors<ProviderValues> };
};
